import logError from 'Helpers/logError';

export type CoachingSuggestion = {
	// Server-assigned stable id (echoed back on dismiss so it persists across reconnect).
	id: string;
	type: 'anchor_close' | 'objection_reframe' | 'deep_dive_offer' | 'value_prop_pivot' | 'stakeholder_risk' | 'next_step';
	title: string;
	rationale: string;
	suggestedPhrase?: string;
	confidence: 'low' | 'medium' | 'high';
	triggeringQuote: string;
};

export type LiveCoachUpcomingMeeting = {
	id: number;
	date: string | null;
	subject: string;
	clientName: string | null;
	notetakerStatus: { state: string; reason?: string };
};

export type LiveCoachSessionContext = {
	startedAt: number | null;
	deal: { annualValue: number; currency: string | null; stageName: string | null } | null;
	participants: string[];
};

export type LiveCoachEvent =
	| { type: 'session_started'; appointmentId: number; userId: number; startedAt: number }
	| { type: 'session_ended'; appointmentId: number }
	| { type: 'transcript'; speaker: string; text: string; seq?: number }
	| { type: 'suggestion'; suggestion: CoachingSuggestion }
	| { type: 'ask_question'; id: string; question: string }
	| { type: 'ask_answer_token'; id: string; delta: string }
	| { type: 'ask_answer'; id: string; answer: string }
	| { type: 'error'; error: string };

type ActiveSession = {
	appointmentId: number;
} | null;

const DISCONNECT_GRACE_MS = 3000;

class LiveCoachResource {
	async getActiveSession(): Promise<ActiveSession> {
		const response = await fetch('/api/v2/live-coach/active');
		if (!response.ok) return null;
		const json = await response.json();
		return json?.data ?? null;
	}

	/**
	 * Every connection starts with a replay of the session so far, and the server rotates the
	 * connection well inside a meeting, so `onOpen` is where a consumer drops what is about to
	 * be sent again. A reconnect within the grace window is that rotation, not an outage.
	 */
	streamSession(
		appointmentId: number,
		onEvent: (event: LiveCoachEvent) => void,
		onError: (err: Error) => void,
		onOpen: () => void
	): () => void {
		const eventSource = new EventSource(`/api/v2/live-coach/stream/${appointmentId}`);
		let grace: ReturnType<typeof setTimeout> | null = null;

		eventSource.onopen = () => {
			if (grace) {
				clearTimeout(grace);
				grace = null;
			}
			onOpen();
		};

		eventSource.onmessage = (e) => {
			try {
				const event: LiveCoachEvent = JSON.parse(e.data);
				onEvent(event);
			} catch (err) {
				console.error('LiveCoachResource: malformed SSE payload', err);
			}
		};

		eventSource.onerror = () => {
			grace ??= setTimeout(() => {
				grace = null;
				onError(new Error('SSE connection error'));
			}, DISCONNECT_GRACE_MS);
		};

		return () => {
			if (grace) {
				clearTimeout(grace);
			}
			eventSource.close();
		};
	}

	async getUpcomingToday(): Promise<LiveCoachUpcomingMeeting[] | null> {
		const response = await fetch('/api/v2/live-coach/upcoming');
		// null, not [] — a server failure must stay distinguishable from an empty
		// day, or the UI renders a confident "no meetings today" over a 500.
		if (!response.ok) {
			logError(new Error(`live-coach/upcoming ${response.status}`), 'LiveCoachResource: upcoming failed');
			return null;
		}
		const json = await response.json();
		return json?.data ?? [];
	}

	async getSessionContext(appointmentId: number): Promise<LiveCoachSessionContext | null> {
		const response = await fetch(`/api/v2/live-coach/context/${appointmentId}`);
		if (!response.ok) return null;
		const json = await response.json();
		return json?.data ?? null;
	}

	async endSession(appointmentId: number): Promise<void> {
		await fetch(`/api/v2/live-coach/end/${appointmentId}`, { method: 'POST' });
	}

	async askLiveCoach(appointmentId: number, question: string): Promise<void> {
		await fetch('/api/v2/live-coach/ask', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ appointmentId, question })
		});
	}

	async insertToNotes(appointmentId: number, text: string): Promise<void> {
		await fetch('/api/v2/live-coach/insert-notes', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ appointmentId, text })
		});
	}

	async dismissSuggestion(appointmentId: number, suggestionId: string, reason?: string): Promise<void> {
		await fetch('/api/v2/live-coach/dismiss', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ appointmentId, suggestionId, reason })
		});
	}
}

export default new LiveCoachResource();
